// "Connect your work email" — shown once to an office person (PM, office, accounting,
// Executive) who opens the desktop app without a mailbox on file. They enter their
// own Kerio address and password; the server checks the login with the mail server
// and keeps it encrypted only if it's accepted. From then on bids, RFIs, submittals,
// change orders and invoices they email go out as them. "Not now" hides it for a week
// on this device. Nobody but the person ever sees the password.
import React, { useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://app.titanelectricreno.com:8080/api';
function getToken() { try { return JSON.parse(localStorage.getItem('titan_session') || 'null')?.token; } catch { return null; } }
const OFFICE = ['ProjectManager', 'OfficeStaff', 'Accounting', 'Executive'];
const SNOOZE_KEY = 'titan_mailbox_prompt_snooze';
const SNOOZE_DAYS = 7;
// When a stored password has stopped working the ask comes back sooner — every day
// until it's fixed, because every email they send is going out as the company meanwhile.
const BROKEN_SNOOZE_KEY = 'titan_mailbox_broken_snooze';
const BROKEN_SNOOZE_DAYS = 1;

async function api(path, opts = {}) {
  const r = await fetch(`${API_BASE}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', 'x-worker-token': getToken(), ...(opts.headers || {}) } });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `Request failed (${r.status})`);
  return j;
}

export default function MailboxPrompt() {
  const [me, setMe] = useState(null);
  const [show, setShow] = useState(false);
  const [mailUser, setMailUser] = useState('');
  const [pass, setPass] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [u, mail] = await Promise.all([api('/me'), api('/mail/status').catch(() => ({ configured: false }))]);
        if (!live) return;
        // Only when the server can send at all, only office roles.
        if (!mail.configured || !OFFICE.includes(u.role)) return;
        const broken = u.hasMail && u.mailBroken;
        if (u.hasMail && !broken) return;                         // connected and working — nothing to ask
        const key = broken ? BROKEN_SNOOZE_KEY : SNOOZE_KEY;
        if (Number(localStorage.getItem(key) || 0) > Date.now()) return;
        setMe(u); setMailUser(u.mailUser || u.email || ''); setShow(true);
      } catch { /* not signed in, or offline — nothing to ask */ }
    })();
    return () => { live = false; };
  }, []);

  if (!show || !me) return null;
  const broken = me.hasMail && me.mailBroken;

  function notNow() {
    try { localStorage.setItem(broken ? BROKEN_SNOOZE_KEY : SNOOZE_KEY, String(Date.now() + (broken ? BROKEN_SNOOZE_DAYS : SNOOZE_DAYS) * 86400000)); } catch {}
    setShow(false);
  }
  async function connect() {
    if (!mailUser.trim() || !pass) { setErr('Enter your work email address and its password.'); return; }
    setBusy(true); setErr('');
    try {
      await api(`/users/${me.id}/mail`, { method: 'PATCH', body: JSON.stringify({ mail_user: mailUser.trim(), mail_pass: pass }) });
      setPass(''); setDone(true);
      setTimeout(() => setShow(false), 2600);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  }

  const inp = { width: '100%', boxSizing: 'border-box', border: '1px solid #d1d5db', borderRadius: 8, padding: '10px 12px', fontSize: 14, fontFamily: 'inherit' };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 6000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: 480, maxWidth: '100%', padding: 24, boxShadow: '0 24px 64px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {done ? (
          <>
            <div style={{ fontSize: 30 }}>✅</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#111827' }}>You're connected</div>
            <div style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.5 }}>Anything you email from the app now goes out from <strong>{mailUser}</strong>, and replies come back to you.</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 30 }}>{broken ? '🔑' : '✉️'}</div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#111827' }}>{broken ? `Your email password stopped working, ${me.firstName}` : `Connect your work email, ${me.firstName}`}</div>
            {broken ? (
              <div style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.55 }}>
                The mail server turned down the app's sign-in to <strong>{me.mailUser}</strong>{me.mailFailedAt ? ` on ${new Date(me.mailFailedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}` : ''} — usually because the password was changed. Until you re-enter it, anything you email from the app goes out from the company mailbox instead of from you.
              </div>
            ) : (
              <div style={{ fontSize: 13.5, color: '#374151', lineHeight: 1.55 }}>
                The app can now email bids, RFIs, submittals, change orders and invoices for you. Enter your Titan mailbox login once and they'll go out
                <strong> from your own address</strong>, with replies coming back to you. The password is checked with the mail server, stored encrypted, and never shown to anyone — not even the office.
              </div>
            )}
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Work email</div>
              <input style={inp} type="email" autoComplete="username" value={mailUser} onChange={e => setMailUser(e.target.value)} placeholder="you@titanelectric.biz" />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>Email password</div>
              <input style={inp} type="password" autoComplete="current-password" value={pass} onChange={e => setPass(e.target.value)} placeholder={broken ? 'Your new email password' : 'The password you use for your Titan email'} onKeyDown={e => { if (e.key === 'Enter') connect(); }} />
            </div>
            {err && <div style={{ fontSize: 13, color: '#b91c1c', fontWeight: 600 }}>{err}</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', marginTop: 4 }}>
              <button onClick={notNow} disabled={busy} style={{ padding: '10px 14px', borderRadius: 9, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>Not now</button>
              <button onClick={connect} disabled={busy || !mailUser.trim() || !pass} style={{ padding: '10px 18px', borderRadius: 9, border: 'none', background: mailUser.trim() && pass ? '#15803d' : '#cbd5e1', color: '#fff', fontSize: 13.5, fontWeight: 800, cursor: mailUser.trim() && pass ? 'pointer' : 'default' }}>
                {busy ? 'Checking with the mail server…' : broken ? 'Update password' : 'Connect'}
              </button>
            </div>
            <div style={{ fontSize: 11.5, color: '#9ca3af' }}>{broken ? 'Not now hides this until tomorrow.' : 'Not now hides this for a week.'} You can also do it later from your card on the Employees page (Executives) or ask an Executive to.</div>
          </>
        )}
      </div>
    </div>
  );
}
